import type { GlobalToken } from "antd";
import { FastColor } from "@ant-design/fast-color";

/** The letter is a flat list of blocks; the builder edits that list. */

export type BlockType =
  | "logo"
  | "header"
  | "title"
  | "text"
  | "image"
  | "imagetext"
  | "button"
  | "divider"
  | "spacer"
  | "social";

/**
 * The language versions a letter is checked in — the locales the app ships
 * with. Shared by the builder and the send step so both offer the same list.
 */
export const LETTER_LANGUAGES = ["en", "de"] as const;

export type LetterLanguage = (typeof LETTER_LANGUAGES)[number];

/** Where something narrower than its block sits across it. */
export const BLOCK_ALIGNS = ["start", "center", "end"] as const;

export type BlockAlign = (typeof BLOCK_ALIGNS)[number];

/** As CSS: what a flex row does with the space around it. */
export const ALIGN_JUSTIFY: Record<BlockAlign, string> = {
  start: "flex-start",
  center: "center",
  end: "flex-end",
};

/**
 * How a picture is sized in an Image block.
 *
 * `original` is the file at its own size, `fill` runs it across the width the
 * block's padding leaves, and `scale` is a share of that width.
 */
export const IMAGE_FITS = ["original", "fill", "scale"] as const;

export type ImageFit = (typeof IMAGE_FITS)[number];

/**
 * The shapes a picture is shown in.
 *
 * Widest first, down to the one portrait: a banner across the top of a letter,
 * a photo, a square, an upright. Not every shape in between — the cropper's own
 * aspect slider runs from 0.5 to 2 in hundredths and names them as bare numbers,
 * which is more choice than a letter has any use for and unreadable besides.
 *
 * One list, read by every picker that asks for a shape: the panel's Ratio field
 * beside an Image + text block, and the crop window for both kinds of picture.
 * They were two lists offering different shapes, and the crop window and the
 * panel disagreed about what a picture could be.
 */
export const IMAGE_RATIOS = ["2:1", "3:2", "1:1", "3:4"] as const;

export type ImageRatio = (typeof IMAGE_RATIOS)[number];

/** What the crop window opens at when nothing else decides: a banner. */
export const DEFAULT_CROP_RATIO: ImageRatio = "2:1";

/**
 * The shape a picture beside text is drawn in until one is chosen. Narrower
 * than a banner because it stands in a column half the letter wide, where a
 * 2:1 strip reads as a rule rather than as a picture.
 *
 * Named rather than repeated: the letter and the panel's Ratio field both fall
 * back to it, and a placeholder drawn at one shape while the field reported
 * another is exactly the drift this file exists to stop.
 */
export const DEFAULT_IMAGETEXT_RATIO: ImageRatio = "3:2";

/**
 * How tall a spacer stands until it is set: one step of the letter's own
 * rhythm. Named because three places had to agree on it — the block as it is
 * created, the letter drawing one whose height a template left unsaid, and the
 * panel's Height field. The panel had been falling back to the *smallest* a
 * spacer may be, so a template's spacer read 24 on the sheet and 12 in the
 * field beside it.
 */
export const SPACER_DEFAULT = 24;

/** The ratio as a number, for a cropper or an aspect-ratio. */
export const ratioValue = (ratio: ImageRatio) => {
  const [width, height] = ratio.split(":").map(Number);

  return width / height;
};

/**
 * The typefaces a letter can be set in, in alphabetical order.
 *
 * A list of stacks, not of fonts. An email is drawn by whatever the reader
 * opens it in, from what is already installed on their machine — a webfont is
 * stripped by Outlook and ignored by several mobile clients — so every entry
 * here names a face that ships with an operating system, together with the
 * fallbacks that cover the machines it does not.
 *
 * Three platforms have to be covered, and they do not behave the same way:
 *
 * - **Desktop** has Arial, Courier New, Georgia, Tahoma, Times New Roman,
 *   Trebuchet MS and Verdana, and then Segoe UI and Calibri or Helvetica Neue
 *   and Palatino depending on which desktop it is.
 * - **iOS** has most of those, but *not* Tahoma, Segoe UI or Calibri, so every
 *   stack naming one of them carries a face iOS does have — Verdana for the
 *   first two, Optima for Calibri.
 * - **Android** has almost none of them. It ships Roboto and Noto and aliases
 *   the classic names to those, so a letter set in Georgia is read in Noto
 *   Serif and one set in Arial in Roboto. Nothing breaks, but the choice mostly
 *   does not survive the trip — which is why the generic at the end of each
 *   stack is the part that decides what an Android reader sees, and why sans
 *   and serif are never mixed inside one stack.
 *
 * Every stack ends in a real face and then a generic, deliberately. Outlook on
 * Windows renders with Word, which does not know `-apple-system` or
 * `BlinkMacSystemFont` and drops to Times New Roman the moment it meets a name
 * it cannot resolve — so a name it can resolve has to come before the end.
 *
 * The names are not translated: a typeface is called the same in every locale,
 * and "Times New Roman" translated would name a font nobody has.
 */
export const EMAIL_FONTS = [
  { key: "arial", label: "Arial", stack: "Arial, Helvetica, sans-serif" },
  // Office's own default since 2007, so it is on most Windows machines and on
  // any Mac with Office installed. Optima carries it on iOS.
  {
    key: "calibri",
    label: "Calibri",
    stack: "Calibri, Candara, Segoe, Optima, Arial, sans-serif",
  },
  {
    key: "courier",
    label: "Courier New",
    stack: "'Courier New', Courier, monospace",
  },
  {
    key: "georgia",
    label: "Georgia",
    stack: "Georgia, 'Times New Roman', serif",
  },
  // macOS and iOS. Falls to Arial, which is what it was drawn against.
  {
    key: "helvetica-neue",
    label: "Helvetica Neue",
    stack: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  },
  // A serif with more contrast than Georgia, under one name or another on both
  // desktops and on iOS — Palatino on Apple, Book Antiqua on Windows.
  {
    key: "palatino",
    label: "Palatino",
    stack: "Palatino, 'Palatino Linotype', 'Book Antiqua', Georgia, serif",
  },
  // Android's own, and Chrome OS's. Named rather than left to the alias table,
  // so an Android reader gets it because it was chosen and not by accident.
  {
    key: "roboto",
    label: "Roboto",
    stack: "Roboto, 'Helvetica Neue', Arial, sans-serif",
  },
  // Windows since Vista, and the face Microsoft's own mail is set in.
  {
    key: "segoe",
    label: "Segoe UI",
    stack: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  // Whatever the reader's own interface is set in: San Francisco on Apple,
  // Segoe UI on Windows, Roboto on Android. The one entry that is native
  // everywhere, and the closest an email gets to looking like the device it is
  // read on.
  {
    key: "system",
    label: "System UI",
    stack:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  },
  { key: "tahoma", label: "Tahoma", stack: "Tahoma, Verdana, sans-serif" },
  {
    key: "times",
    label: "Times New Roman",
    stack: "'Times New Roman', Times, serif",
  },
  {
    key: "trebuchet",
    label: "Trebuchet MS",
    stack: "'Trebuchet MS', Helvetica, sans-serif",
  },
  { key: "verdana", label: "Verdana", stack: "Verdana, Geneva, sans-serif" },
] as const;

/**
 * What a letter opens in. Named rather than taken from the front of the list,
 * which is sorted alphabetically and could be reordered without anyone meaning
 * to change this.
 */
export const DEFAULT_FONT =
  EMAIL_FONTS.find((font) => font.key === "roboto") ?? EMAIL_FONTS[0];

/** Space on each of the four sides. */
export interface Edges {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/** The four corners, clockwise from the top left. */
export interface Corners {
  topLeft: number;
  topRight: number;
  bottomRight: number;
  bottomLeft: number;
}

/** As CSS: four values, clockwise from the top. */
export const edgesCss = ({ top, right, bottom, left }: Edges) =>
  `${top}px ${right}px ${bottom}px ${left}px`;

/** As CSS: four values, clockwise from the top left. */
export const cornersCss = ({
  topLeft,
  topRight,
  bottomRight,
  bottomLeft,
}: Corners) => `${topLeft}px ${topRight}px ${bottomRight}px ${bottomLeft}px`;

/** The same number on all four, which is what a letter starts with. */
export const evenCorners = (radius: number): Corners => ({
  topLeft: radius,
  topRight: radius,
  bottomRight: radius,
  bottomLeft: radius,
});

/**
 * The top two corners, and the bottom two.
 *
 * A card in a letter is not one block: it is a run of them sharing a ground,
 * and the run is what has corners. The first section rounds its top, the last
 * its bottom, and everything between stays square — which is how a heading, a
 * paragraph and a button come to read as one card.
 */
export const topCorners = (radius: number): Corners => ({
  ...evenCorners(0),
  topLeft: radius,
  topRight: radius,
});

export const bottomCorners = (radius: number): Corners => ({
  ...evenCorners(0),
  bottomRight: radius,
  bottomLeft: radius,
});

/**
 * What a section is called once it is in the letter.
 *
 * Heading and Paragraph are one section — Text — offered twice in the Blocks
 * panel so that adding one can preset the style it opens at. Past that point
 * the letter, the editor and the drag label all call it by the same name.
 */
export const sectionTypeKey = (type: BlockType): BlockType =>
  type === "header" ? "text" : type;

/**
 * The three sizes a call-to-action button comes in. `size` on the block is the
 * height; the width comes with it, so a button keeps its proportions rather
 * than stretching to whatever its label is.
 */
export const BUTTON_SIZES = [
  { height: 36, width: 120, fontSize: 14, label: "sizeSmall" },
  { height: 44, width: 180, fontSize: 16, label: "sizeMedium" },
  { height: 52, width: 240, fontSize: 18, label: "sizeLarge" },
] as const;

/**
 * The three sides a social button comes in, in pixels — the side of the square,
 * since they are square. Named here rather than in the panel so the letter and
 * the panel cannot disagree about what a section with no size of its own is.
 *
 * Medium is the default: 40 is still a comfortable target for a finger, and a
 * row of eight at 48 was wider than the letter had room for.
 */
export const SOCIAL_SIZES = [
  { value: 32, label: "sizeSmall" },
  { value: 40, label: "sizeMedium" },
  { value: 48, label: "sizeLarge" },
] as const;

export const DEFAULT_SOCIAL_SIZE = SOCIAL_SIZES[1].value;

/**
 * The smallest square a finger hits reliably: 44px, which is Apple's minimum in
 * the Human Interface Guidelines and the number email guidance repeats.
 */
export const TOUCH_TARGET = 44;

/**
 * How much air a social button keeps on each side.
 *
 * A small icon is not a smaller target — it is a target that misses. Below 44px
 * the button borrows the difference from the space around it, so two icons are
 * never closer than a fingertip apart however small they are drawn. Above it,
 * the letter's own 4px is enough and the row stays a row rather than drifting
 * apart.
 */
export const socialGap = (side: number) =>
  Math.max(4, Math.round((TOUCH_TARGET - side) / 2));

/** What a call-to-action button's corners start at, before a section says. */
export const BUTTON_RADIUS = 6;

/** The size a button's height belongs to, medium if it matches none. */
export const buttonSize = (height?: number) =>
  BUTTON_SIZES.find((size) => size.height === height) ?? BUTTON_SIZES[1];

/** The letter's colours a divider can be drawn in. */
export const LINE_COLOURS = [
  "border",
  "borderOnDark",
  "textOnBackground",
  "textOnAccent",
  "textOnBrand",
  "linkOnAccent",
  "brand",
] as const;

export type LineColour = (typeof LINE_COLOURS)[number];

/** The letter's colours a block's words or icons can be set in. */
export const INK_COLOURS = [
  "textOnBackground",
  "textMutedOnBackground",
  "textOnAccent",
  "textMutedOnAccent",
  "textOnBrand",
  "brand",
  "link",
  "linkOnAccent",
] as const;

export type InkColour = (typeof INK_COLOURS)[number];

/**
 * Which colours a block is offered for its words, its links and its rules,
 * given the ground it sits on.
 *
 * A button reads from the same table: it makes its own ground out of its fill,
 * because the label stands on the fill and not on the block behind it.
 *
 * A block on the sheet has no use for the accent's text colours, and one on the
 * accent has no use for the sheet's — offering all of them turns a choice into
 * a search, and most of the swatches would be unreadable where they landed. A
 * ground picked by hand has no pairing of its own, so it is given both halves
 * of each pair — light and dark, each with its quiet one — and the user
 * decides.
 */
export const INK_CHOICES: Record<string, InkColour[]> = {
  // Text colours and the link colour, on each of the letter's own grounds. No
  // brand among them: the brand is what a button is made of, and words set in
  // it read as a link that does not lead anywhere — the link colour is the one
  // meant for words that do.
  light: ["textOnBackground", "textMutedOnBackground", "link"],
  accent: ["textOnAccent", "textMutedOnAccent", "linkOnAccent"],
  brand: ["textOnBrand"],
  // A ground picked by hand has no pairing of its own, and the letter cannot
  // know how dark it is. It gets both halves of each pair — the sheet's dark
  // and its quiet one, the accent's light and its quiet one — so a caption on a
  // hand-picked ground has the same second voice it has on the letter's own,
  // and the brand. Past that, the picker.
  custom: [
    "textOnBackground",
    "textMutedOnBackground",
    "textOnAccent",
    "textMutedOnAccent",
    "brand",
  ],
};

export const LINE_CHOICES: Record<string, LineColour[]> = {
  light: ["border", "textOnBackground", "brand"],
  accent: ["borderOnDark", "textOnAccent", "linkOnAccent"],
  // The brand is a dark ground like the accent, so it takes the same rule
  // colours — but not the brand itself, which is what it is drawn on.
  brand: ["borderOnDark", "textOnBrand"],
  custom: [...LINE_COLOURS],
};

/**
 * The same colour, said for the other ground.
 *
 * A block moved from the sheet to the accent keeps the colour it was given —
 * but the colour it was given was chosen for the sheet, and on the accent it is
 * the wrong half of a pair. Swapping it for its opposite number keeps the
 * decision and moves it, rather than dropping the user back to a default.
 */
export const COLOUR_ANALOGUE: Record<string, string> = {
  textOnBackground: "textOnAccent",
  textOnAccent: "textOnBackground",
  textMutedOnBackground: "textMutedOnAccent",
  textMutedOnAccent: "textMutedOnBackground",
  link: "linkOnAccent",
  linkOnAccent: "link",
  border: "borderOnDark",
  borderOnDark: "border",
};

/**
 * Which of a block's colours have to move when its ground does, and what they
 * become. Only what the block has actually been given is touched: a colour it
 * never chose stays unset, and the letter goes on pairing it by the ground.
 *
 * A colour that is still offered on the new ground stays. One that has an
 * opposite number there — the sheet's dark for the accent's light, and back —
 * is swapped for it, which keeps the decision rather than dropping the user to
 * a default. One that has neither is cleared, and the pairing takes over: that
 * is what happens on the brand, which offers only the one colour meant for
 * words on it.
 */
export function coloursForGround(
  block: Block,
  ground: string,
): Pick<Block, "ink" | "line"> {
  const moved = <Key extends string>(key: Key | undefined, offered: Key[]) => {
    if (!key) return undefined;
    if (offered.includes(key)) return key;

    const analogue = COLOUR_ANALOGUE[key] as Key | undefined;

    return analogue && offered.includes(analogue) ? analogue : undefined;
  };

  return {
    ink: moved(block.ink, INK_CHOICES[ground] ?? INK_CHOICES.light),
    line: moved(block.line, LINE_CHOICES[ground] ?? LINE_CHOICES.light),
  };
}

/**
 * What a block's words are set in on each of the letter's grounds, and on a
 * button's fill. Stated here so the letter, the editor and the preview pair
 * them the same way — and so changing one of the six colours cannot leave a
 * pairing behind.
 */
export const INK_ON: Record<string, InkColour> = {
  Default: "textOnBackground",
  Soft: "textOnBackground",
  // The page colour, borrowed as a block's ground. It is a light one like the
  // sheet's own, so everything on it reads the way it does on the sheet.
  Email: "textOnBackground",
  Accent: "textOnAccent",
  Brand: "textOnBrand",
  brand: "textOnBrand",
  accent: "textOnAccent",
  emailBackground: "textOnBackground",
  subtleBackground: "textOnBackground",
  background: "textOnBackground",
};

/**
 * Which of the letter's grounds a block stands on.
 *
 * One function, read by the panel to decide what colours to offer and by the
 * letter to decide what to draw when the block has chosen none. Two copies of
 * this is how the panel came to mark one colour as chosen while the letter drew
 * another.
 */
export type Ground = "light" | "accent" | "brand" | "custom";

export const groundOf = ({
  bg,
  bgCustom,
}: Pick<Block, "bg" | "bgCustom">): Ground => {
  if (bgCustom) return "custom";
  if (bg === "Accent") return "accent";
  if (bg === "Brand") return "brand";

  return "light";
};

/**
 * What the words on each ground are set in — and an icon with them: an icon is
 * a word in a section, so Icon color offers exactly what Text offers.
 *
 * A ground picked by hand has no pairing of its own; the letter cannot know how
 * dark it is, so it keeps the sheet's.
 */
export const GROUND_INK: Record<Ground, InkColour> = {
  light: "textOnBackground",
  accent: "textOnAccent",
  brand: "textOnBrand",
  custom: "textOnBackground",
};

/**
 * What an edge is drawn in on each ground — a divider across the letter, the
 * outline of a social button.
 *
 * The brand carries no rule colour of its own, and the accent's translucent
 * white is not it: on the brand an edge takes the same colour as the words.
 */
export const GROUND_LINE: Record<Ground, LineColour> = {
  light: "border",
  accent: "borderOnDark",
  brand: "textOnBrand",
  custom: "border",
};

/** The letter's colours a button can be filled with. */
export const FILL_COLOURS = [
  "brand",
  "accent",
  // Strongest to lightest, the same run the block grounds are offered in: the
  // page colour sits a step above the sheet's quiet one.
  "emailBackground",
  "subtleBackground",
  "background",
] as const;

export type FillColour = (typeof FILL_COLOURS)[number];

/**
 * How a button or a social icon is drawn: the mark on its own, a filled shape
 * behind it, or an outlined one.
 *
 * One setting for both, because it is one decision. A call-to-action and a row
 * of social icons are the same thing at different sizes — something to press —
 * and offering "filled or outlined" twice, under two names and with two
 * behaviours, is how the two drift apart.
 */
export const BLOCK_VARIANTS = ["plain", "fill", "outline"] as const;

export type BlockVariant = (typeof BLOCK_VARIANTS)[number];

/** What each block type is drawn as before anything is chosen. */
export const DEFAULT_VARIANT: Record<string, BlockVariant> = {
  button: "fill",
  social: "fill",
};

/**
 * Which of the three each block type is offered.
 *
 * A social icon on its own is a shape people know — a row of bare marks under a
 * letter reads as a footer. A call-to-action with neither fill nor outline is
 * just a line of text, and a letter already has a way to write a link; offering
 * it as a button would be offering a button that is not one.
 */
export const VARIANTS_FOR: Record<string, BlockVariant[]> = {
  button: ["fill", "outline"],
  social: [...BLOCK_VARIANTS],
};

export interface Block {
  id: string;
  type: BlockType;
  text?: string;
  /** button, image and imagetext: where the section leads when clicked */
  link?: string;
  /** image: how the picture is sized in the block */
  fit?: ImageFit;
  /** `scale` only: how much of the available width the picture takes, in %. */
  scale?: number;
  /** image at less than full width: where it sits across the block */
  imageAlign?: BlockAlign;
  /** imagetext: the shape the picture is shown in */
  ratio?: ImageRatio;
  /** What a screen reader reads instead of the picture. image and imagetext. */
  alt?: string;
  /** The rows a social block renders, in the order they are shown. */
  socials?: SocialLink[];
  /** divider thickness, spacer height, and the side of a social icon button */
  size?: number;
  /**
   * The space a section keeps around its content — above and below, and to the
   * sides. Undefined follows the letter's own setting, which is what a new
   * section does.
   */
  padding?: Edges;
  /** The section's own corners; undefined follows the letter. */
  radius?: Corners;
  /**
   * The corners of what sits inside the section rather than of the section
   * itself — a picture, a social button. Undefined follows the letter.
   */
  itemRadius?: Corners;
  /** imagetext: which side the picture is on. */
  imageSide?: "left" | "right";
  /**
   * The picture itself, as a data URL.
   *
   * TODO(design): held in the letter because there is nowhere to upload to. A
   * developer replaces this with whatever the API returns for an uploaded
   * image. See DEVELOPER_TASKS.md.
   */
  image?: string;
  /**
   * button and social: how it is drawn — the label or glyph on its own, a
   * filled shape behind it, or an outlined one. Undefined follows
   * DEFAULT_VARIANT, which is filled for a button and outlined for a row of
   * icons.
   */
  variant?: BlockVariant;
  /**
   * Which of the letter's three grounds the section sits on: its base, a soft
   * tone between that and the accent, or the accent itself.
   */
  bg?: "Default" | "Soft" | "Email" | "Accent" | "Brand";
  /**
   * A ground of the section's own, outside the letter's scheme. Set, it wins
   * over `bg` — which is kept, so clearing the colour returns the section to
   * whichever of the three it was on.
   */
  bgCustom?: string;
  /**
   * Which of the letter's colours the section's words or icons are set in.
   * Undefined follows the ground it sits on: light on the accent, the letter's
   * own text colour elsewhere.
   */
  ink?: InkColour;
  /** A colour of the section's own for the same, which wins over `ink`. */
  inkCustom?: string;
  /** button: where it sits across the block. */
  align?: BlockAlign;
  /** button and social: which of the letter's colours fills the button. */
  fill?: FillColour;
  /** A colour of the section's own for the same, which wins over `fill`. */
  fillCustom?: string;
  /** divider only: which of the letter's colours the rule is drawn in. */
  line?: LineColour;
  /** divider only: a colour of its own, which wins over `line`. */
  lineCustom?: string;
}

/**
 * What the Blocks panel offers. `logo` is missing on purpose: a letter has one,
 * it comes from the template, and adding a second makes no sense.
 */
export const BLOCK_KINDS: { type: BlockType; sample: string }[] = [
  { type: "header", sample: "Heading" },
  { type: "text", sample: "Write something worth reading here." },
  { type: "image", sample: "" },
  { type: "imagetext", sample: "Image with caption" },
  { type: "button", sample: "CTA button" },
  { type: "divider", sample: "" },
  { type: "spacer", sample: "" },
  // No sample text: a social section is its icons.
  { type: "social", sample: "" },
];

let seq = 0;
const nextId = () => `blk-${(seq += 1)}`;

/** The networks a social row can point at — those antd ships an icon for. */
export const SOCIAL_NETWORKS = [
  "facebook",
  "instagram",
  "x",
  "linkedin",
  "youtube",
  "tiktok",
  "whatsapp",
  "telegram",
] as const;

export type SocialNetwork = (typeof SOCIAL_NETWORKS)[number];

export interface SocialLink {
  id: string;
  network: SocialNetwork;
  url: string;
  /** Read in place of the icon, and shown if the image cannot load. */
  alt: string;
}

export function createSocial(network: SocialNetwork = "facebook"): SocialLink {
  return { id: nextId(), network, url: "https://", alt: network };
}

/**
 * The network a new row opens at: the first the section does not already show.
 *
 * A second Facebook is almost never what was meant, and carrying on down the
 * list saves reaching for the select on every row. Once all eight are in there
 * is nothing left to suggest, so it starts over at the first — a duplicate is
 * then a deliberate choice rather than a default.
 */
export const nextSocialNetwork = (rows: SocialLink[]): SocialNetwork =>
  SOCIAL_NETWORKS.find(
    (network) => !rows.some((row) => row.network === network),
  ) ?? SOCIAL_NETWORKS[0];

/**
 * Whether a link is one an email client will follow.
 *
 * Deliberately not a strict URL check. Nothing is sent from the builder, and a
 * field that argues with every half-typed address is worse than no check at
 * all. It catches the three mistakes that actually reach a letter: an address
 * with no scheme, which an email client reads as relative and cannot follow; a
 * host with no dot; and a space in the middle.
 *
 * Empty passes — a link is optional on every section that has one — and so does
 * the bare "https://" a new section starts with, which is a prompt rather than
 * an answer.
 *
 * TODO(design): this is feedback in the panel, not validation. Whatever the
 * send step and the API enforce is a developer's to write, and this must not be
 * mistaken for it. See DEVELOPER_TASKS.md.
 */
export const isLinkValid = (value?: string): boolean => {
  const link = (value ?? "").trim();

  if (!link || /^(https?:\/\/|mailto:)$/i.test(link)) return true;
  if (/\s/.test(link)) return false;

  try {
    const url = new URL(link);

    if (url.protocol === "mailto:") {
      return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(url.pathname);
    }

    return (
      (url.protocol === "http:" || url.protocol === "https:") &&
      url.hostname.includes(".")
    );
  } catch {
    // Not a URL at all — most often a domain typed without its scheme.
    return false;
  }
};

export function createBlock(type: BlockType): Block {
  const kind = BLOCK_KINDS.find((k) => k.type === type);

  return {
    id: nextId(),
    type,
    text: kind?.sample ?? "",
    ...(type === "image" || type === "imagetext"
      ? { fit: "fill" as const }
      : {}),
    ...(type === "button"
      ? {
          link: "https://",
          // Medium, centred and filled. These were already what the letter and
          // the panel each fell back to when the block said nothing, in three
          // separate places — written on the block instead, there is one answer
          // rather than three that happen to agree.
          size: BUTTON_SIZES[1].height,
          align: "center" as const,
          variant: DEFAULT_VARIANT.button,
        }
      : {}),
    ...(type === "spacer" ? { size: SPACER_DEFAULT } : {}),
    ...(type === "divider" ? { size: 1 } : {}),
    ...(type === "social"
      ? {
          size: DEFAULT_SOCIAL_SIZE,
          variant: DEFAULT_VARIANT.social,
          // The same fill a button opens at, and the same one the panel shows
          // as chosen. Written on the block rather than left to a fallback:
          // that is how the two came to disagree before.
          fill: "brand" as const,
          socials: [
            createSocial("facebook"),
            createSocial("instagram"),
            createSocial("x"),
          ],
        }
      : {}),
  };
}

/** A block of `type`, with whatever the template wants said about it. */
const block = (type: BlockType, patch: Partial<Block> = {}): Block => ({
  ...createBlock(type),
  ...patch,
});

/**
 * A letter's wordmark: the picture at its own size, centred, rather than a
 * band of text. Every template opens with one, and so does a blank letter —
 * a logo is a file the customer supplies, not something typed in.
 */
const logo = () =>
  block("image", {
    fit: "original",
    alt: "Company logo",
  });

/** What a brand new campaign starts from. */
export const defaultLetter = (): Block[] => [
  logo(),
  block("header", { text: "A heading for your campaign" }),
  block("text", { text: "Write something worth reading here." }),
  block("image"),
  block("button", { text: "Read more" }),
];

/**
 * Blocks behind each entry in EMAIL_TEMPLATES — three letters of the kind a
 * customer actually sends: something for sale, a first hello, and the month's
 * round-up. Each is a complete letter, from the wordmark at the top to the
 * social row at the foot, so a campaign started from one has nothing missing.
 */
/**
 * The space a section keeps when it is the top, the middle or the foot of a
 * band. A band is a run of sections sharing one ground, so the run's own air
 * lives on the first and last of them — the middles keep the letter's setting.
 */
const bandTop = { top: 32, right: 32, bottom: 8, left: 32 };
const bandMiddle = { top: 8, right: 32, bottom: 8, left: 32 };
const bandFoot = { top: 8, right: 32, bottom: 32, left: 32 };

/** How round a card in a letter is. */
const CARD_RADIUS = 12;

/**
 * The rounder one, for templates made of cards. Where a card is the unit rather
 * than the exception, the corner is what tells it apart from the sheet — and at
 * 12 it read as a rounded rectangle rather than as a card lying on something.
 */
const ROUND_RADIUS = 24;

/**
 * A card that holds one entry rather than a whole run keeps less air: the words
 * are short, and 32 either side made a list of them read as a list of pages.
 */
const entryTop = { top: 20, right: 20, bottom: 8, left: 20 };
const entryFoot = { top: 8, right: 20, bottom: 20, left: 20 };

/**
 * The gap before the foot of a letter. Wide enough that the sign-off reads as a
 * different thing from what it follows rather than as one more section.
 */
const FOOTER_GAP = 56;

/**
 * How every letter ends: a gap, then a dark run carrying the social icons and
 * the line that says why this arrived.
 *
 * One footer rather than one per template. A letter's foot is the part a reader
 * has seen a hundred times and stops reading — it should be the same every
 * time, and a template that reinvented it was making a decision nobody asked
 * for. The run rounds where it begins and stops square where the letter does:
 * it is the end of the letter, not a card lying on something.
 *
 * The exception is a letter built of full-width bands, where the change of
 * ground already says the letter has ended and a gap would break the stack.
 */
const footer = (radius: number = CARD_RADIUS): Block[] => [
  block("spacer", { size: FOOTER_GAP }),
  block("social", {
    bg: "Accent",
    variant: "fill",
    padding: bandTop,
    radius: topCorners(radius),
  }),
  block("text", {
    text: "You are receiving this because you asked to.\n\nUnsubscribe at any time.",
    bg: "Accent",
    padding: bandFoot,
  }),
];

/**
 * What a template changes about the letter itself, beyond its sections.
 *
 * Most templates take the letter as it comes. One that does not says so here
 * rather than by making the user undo a default: a card stack wants nothing
 * behind the cards, so the ground around the letter is the sheet's own.
 *
 * Written as a function of the defaults rather than as a colour, so a template
 * says what it means — "the same as the sheet" — and follows a rebrand with
 * everything else.
 */
export const templateStyles: Record<
  string,
  (defaults: EmailStyles) => Partial<EmailStyles>
> = {
  cards: (defaults) => ({ emailBackground: defaults.background }),
};

export const templateBlocks: Record<string, () => Block[]> = {
  /**
   * Bands the width of the letter, told apart by their ground alone: a dark
   * one to open, a picture across the whole sheet, a brand one for the second
   * offer, a tinted one to sign off. Nothing is rounded and nothing is inset —
   * the letter is read as a stack of surfaces.
   */
  bands: () => [
    block("image", {
      fit: "original",
      alt: "Company logo",
      bg: "Accent",
      padding: bandTop,
    }),
    block("divider", { bg: "Accent", padding: bandMiddle }),
    block("header", {
      text: "One thing worth your attention",
      bg: "Accent",
      padding: bandMiddle,
    }),
    block("text", {
      text: "A line or two saying what it is, and why it is here rather than anywhere else.",
      bg: "Accent",
      padding: bandMiddle,
    }),
    block("button", {
      text: "See it",
      bg: "Accent",
      align: "start",
      padding: bandFoot,
    }),

    // Edge to edge: no padding at all, so the picture is the band.
    block("image", {
      alt: "The thing itself",
      padding: { top: 0, right: 0, bottom: 0, left: 0 },
    }),

    block("header", {
      text: "And a second reason",
      bg: "Brand",
      padding: bandTop,
    }),
    block("text", {
      text: "The same shape as the band above it, in the colour the letter keeps for what it wants noticed.",
      bg: "Brand",
      padding: bandMiddle,
    }),
    // A light button on the brand: the fill is the sheet's own ground, so the
    // label pairs to the sheet's text colour.
    block("button", {
      text: "Take a look",
      bg: "Brand",
      fill: "background",
      align: "start",
      padding: bandFoot,
    }),

    block("social", { bg: "Soft", padding: bandTop }),
    block("text", {
      text: "You are receiving this because you asked to.\n\nUnsubscribe at any time.",
      bg: "Soft",
      padding: bandFoot,
    }),
  ],

  /**
   * The same letter as `bands`, built as cards instead: every run is inset from
   * the sheet and rounded, so the white between them is part of the design
   * rather than the end of it. The corners belong to the run — the first
   * section rounds its top, the last its bottom.
   */
  cards: () => [
    logo(),

    block("header", {
      text: "One thing worth your attention",
      bg: "Accent",
      padding: bandTop,
      radius: topCorners(ROUND_RADIUS),
    }),
    block("text", {
      text: "A line or two saying what it is, and why it is here rather than anywhere else.",
      bg: "Accent",
      padding: bandMiddle,
    }),
    block("button", {
      text: "See it",
      bg: "Accent",
      align: "start",
      padding: bandFoot,
      radius: bottomCorners(ROUND_RADIUS),
    }),

    // Edge to edge across the sheet, the same as the coloured runs either side
    // of it: a section's ground always reaches the sheet's edges, so a picture
    // inset from them would be the one thing in this letter that is.
    block("image", {
      alt: "The thing itself",
      padding: { top: 12, right: 0, bottom: 12, left: 0 },
      itemRadius: evenCorners(ROUND_RADIUS),
    }),

    block("header", {
      text: "And a second reason",
      bg: "Brand",
      padding: bandTop,
      radius: topCorners(ROUND_RADIUS),
    }),
    block("text", {
      text: "The same card, in the colour the letter keeps for what it wants noticed.",
      bg: "Brand",
      padding: bandMiddle,
    }),
    block("button", {
      text: "Take a look",
      bg: "Brand",
      fill: "background",
      align: "start",
      padding: bandFoot,
      radius: bottomCorners(ROUND_RADIUS),
    }),

    ...footer(ROUND_RADIUS),
  ],

  /**
   * A letter that lists things. Each entry is a tinted card — a picture beside
   * its words, and the button that opens it — and the two cards are the whole
   * middle of the letter. It signs off on the dark ground.
   */
  listing: () => [
    logo(),
    block("header", { text: "Three things this month" }),
    block("text", {
      text: "The short version, with a way into each one.",
    }),

    // The picture leads in both, so the eye runs down one edge rather than
    // zig-zagging between them.
    block("imagetext", {
      text: "The first entry, in as many words as it takes to say whether it is worth opening.",
      alt: "The first entry",
      bg: "Soft",
      padding: entryTop,
      radius: topCorners(ROUND_RADIUS),
    }),
    block("button", {
      text: "Read it",
      bg: "Soft",
      align: "end",
      // Small: a button that opens one entry in a list is not the letter's
      // call to action, and at medium it outweighed the entry it belongs to.
      size: BUTTON_SIZES[0].height,
      padding: entryFoot,
      radius: bottomCorners(ROUND_RADIUS),
    }),

    // Between the two cards, and nothing else: they share a ground, so without
    // a gap they would run together into one card twice as tall.
    block("spacer", { size: 16 }),

    block("imagetext", {
      text: "The second, the same shape, so a reader learns the pattern once and uses it all the way down.",
      alt: "The second entry",
      bg: "Soft",
      padding: entryTop,
      radius: topCorners(ROUND_RADIUS),
    }),
    block("button", {
      text: "Read it",
      bg: "Soft",
      align: "end",
      // Small: a button that opens one entry in a list is not the letter's
      // call to action, and at medium it outweighed the entry it belongs to.
      size: BUTTON_SIZES[0].height,
      padding: entryFoot,
      radius: bottomCorners(ROUND_RADIUS),
    }),

    block("text", {
      text: "And everything else that happened, in one place.",
    }),
    block("button", { text: "See the rest" }),

    ...footer(ROUND_RADIUS),
  ],

  /**
   * One story and nothing else: the picture, what it is about, and the way in.
   * Centred throughout, and closed by a dark card rather than a band — the foot
   * of the letter is a thing of its own.
   */
  story: () => [
    logo(),
    block("image", {
      alt: "The story",
      itemRadius: evenCorners(CARD_RADIUS),
    }),
    block("title", { text: "What happened" }),
    block("text", {
      text: "Two or three lines that say enough for a reader to decide, and not so many that they decide here instead.",
    }),
    block("button", { text: "Read the story" }),

    ...footer(),
  ],

  /**
   * The plainest of the five: everything on the sheet's own ground, spaced
   * rather than coloured. A row of links under the wordmark and another at the
   * foot stand in for a letter that is part of something larger.
   *
   * TODO(design): those two rows are paragraphs. The builder has no block for a
   * row of links — a real one would centre them, space them evenly and give
   * each its own address. See DEVELOPER_TASKS.md.
   */
  plain: () => [
    logo(),
    block("text", { text: "Product   ·   Pricing   ·   Docs   ·   Support" }),
    block("divider"),

    block("image", { alt: "This issue" }),
    block("title", { text: "This issue" }),
    block("text", {
      text: "What is in it, in the order a reader would want it.\n\nShort enough that the button below is still on the screen.",
    }),
    block("button", { text: "Open it" }),

    block("divider"),
    block("text", { text: "Blog   ·   Changelog   ·   Status   ·   Careers" }),
    ...footer(),
  ],
  promo: () => [
    logo(),
    block("header", { text: "The new collection is here" }),
    block("image", { alt: "The new collection" }),
    block("text", {
      text: "Three days only.\n\nEverything you have been waiting for, at the best price it will be this year. Free delivery on anything over £50.",
    }),
    block("button", { text: "Shop the sale" }),
    ...footer(),
  ],

  welcome: () => [
    logo(),
    block("header", { text: "Welcome aboard" }),
    block("text", {
      text: "We are glad you are here.\n\nYour account is ready. Here is the short version of what to do first — it takes about five minutes.",
    }),
    block("imagetext", {
      text: "Set up your profile so the people you work with know who they are talking to.",
      alt: "Setting up a profile",
    }),
    block("imagetext", {
      text: "Invite your team. Everything you build is theirs to see from the moment they join.",
      alt: "Inviting a team",
    }),
    block("button", { text: "Open your account" }),
    ...footer(),
  ],

  digest: () => [
    logo(),
    block("header", { text: "Company chronicles" }),
    block("text", {
      text: "Everything worth knowing from the past month, in the time it takes to finish a coffee.",
    }),
    block("divider"),
    block("title", { text: "The story of the month" }),
    block("imagetext", {
      text: "How we rebuilt the thing everybody uses, and what it cost us to get it right.",
      alt: "The story of the month",
    }),
    block("button", { text: "Read the full story" }),
    block("divider"),
    block("title", { text: "In case you missed it" }),
    block("text", {
      text: "Three smaller changes shipped this month.\n\nFaster search, a calendar that finally exports, and a settings screen nobody has to be shown twice.",
    }),
    ...footer(),
  ],
};

/**
 * The letter's own palette: six colours, derived from the admin panel's brand
 * colour and editable from there on.
 *
 * Everything a letter draws is one of these. A block can carry a colour of its
 * own outside them, but the scheme is what the swatches offer and what a new
 * letter is built from.
 */
export interface EmailStyles {
  /**
   * Words on the sheet, and on the quieter ground beside it.
   *
   * Named for the ground it is read on rather than for being dark: all six of
   * these are the user's to change, and a "dark" colour set to something pale
   * would leave every pairing in the letter wrong.
   */
  textOnBackground: string;
  /** The quieter half of that: a caption, a note, small print. */
  textMutedOnBackground: string;
  /** Words on the accent ground. */
  textOnAccent: string;
  /** The quieter half of that. */
  textMutedOnAccent: string;
  /** Words on the brand colour — a button's label. */
  textOnBrand: string;
  /** The brand: buttons, and anything the letter wants noticed. */
  brand: string;
  /** Words that lead somewhere. Often the brand, but not always — a link has to
   * hold its own against the text around it. */
  link: string;
  /** The same on the accent ground, where the light one would be lost. */
  linkOnAccent: string;
  /** A strong ground for a block that has to carry weight. */
  accent: string;
  /**
   * The ground the letter stands on rather than the letter itself — what an
   * email client paints behind the content column, and what shows beside a
   * 640px letter on a wide screen and below it wherever it ends.
   */
  emailBackground: string;
  /** The sheet, and any block that has not asked for another ground. */
  background: string;
  /** A quieter ground, a step from the sheet without the weight of the accent. */
  subtleBackground: string;
  /** Rules and outlines: a divider, the edge of an outlined button. */
  border: string;
  /** The same on a dark ground, where the light one disappears. */
  borderOnDark: string;
  /** How round a block's corners are, in pixels, one value per corner. */
  radius: Corners;
  /** The same for the pictures inside them, which are asked for separately. */
  imageRadius: Corners;
  /** The space every block keeps around its content, side by side. */
  padding: Edges;
  /**
   * What the whole letter is set in, as a CSS font stack from EMAIL_FONTS.
   * One typeface for the letter rather than one per section: an email that
   * changes face halfway down reads as two emails.
   */
  fontFamily: string;
}

/**
 * What a new letter starts from, read out of the admin panel's own theme so a
 * change of brand carries into the letters. Every one of these is a default:
 * the style panel writes over any of them.
 *
 *   Base   the container white — the sheet, and every section that has not
 *          asked for another ground
 *   Soft   the brand's tinted background, its lightest step
 *   Accent antd's darkest neutral surface — the one its tooltips sit on — for
 *          a section that has to carry weight; its text is light, hence
 *          accentText beside it
 */
export function defaultEmailStyles(token: GlobalToken): EmailStyles {
  return {
    // Four of the six come from the brand colour by way of antd's palette:
    // colorPrimary is the brand, colorPrimaryBg its lightest step, and the two
    // text colours are the pair antd uses on light and dark grounds. The accent
    // is antd's own darkest surface — a ground that has to carry weight without
    // becoming a second brand colour.
    textOnBackground: token.colorText,
    textMutedOnBackground: token.colorTextDescription,
    textOnAccent: token.colorTextLightSolid,
    // antd has no muted colour for a dark ground: its light solid at 65% is the
    // same relationship colorTextDescription has to the dark one.
    textMutedOnAccent: new FastColor(token.colorTextLightSolid)
      .setA(0.65)
      .toRgbString(),
    textOnBrand: token.colorTextLightSolid,
    brand: token.colorPrimary,
    link: token.colorLink,
    // One step lighter than the brand — antd's primary-5, which it calls
    // colorPrimaryHover. A link has to stay recognisably the brand on the
    // accent, and the brand itself sits too dark against it.
    linkOnAccent: token.colorPrimaryHover,
    accent: token.colorBgSpotlight,
    // A tint of the brand rather than white, so the sheet reads as a sheet
    // standing on something rather than as the whole window. antd's
    // primary-2 (colorPrimaryBgHover, #bae0ff at the default blue): one step
    // up from primary-1, which the letter already uses as its Subtle ground —
    // the two have to be told apart, since a Subtle block sitting on this is
    // exactly the case that would otherwise disappear.
    emailBackground: token.colorPrimaryBgHover,
    background: token.colorBgContainer,
    subtleBackground: token.colorPrimaryBg,
    border: token.colorSplit,
    // A rule on the accent: the light text colour worn thin, the same way
    // colorSplit is the dark one worn thin on a light ground.
    borderOnDark: new FastColor(token.colorTextLightSolid)
      .setA(0.24)
      .toRgbString(),
    // Square by default, sections and pictures alike: a letter is a stack of
    // bands, and a curve on each one is a decision rather than a starting
    // point.
    radius: evenCorners(0),
    imageRadius: evenCorners(0),
    // 12 above and below, 24 at the sides: a letter is read down the page, so
    // the space between one section and the next is tighter than the margin
    // that holds its text off the edge of the sheet.
    padding: { top: 12, right: 24, bottom: 12, left: 24 },
    // Roboto, not the app's own font. antd's default stack starts at the
    // system UI face, which is right for an interface and unavailable to an
    // email client — a letter set in it would be drawn in whatever each reader
    // fell back to.
    //
    // TODO(design): Roboto is native on Android and falls to Helvetica Neue on
    // Apple and Arial on Windows, so it reads as a modern sans nearly
    // everywhere. The exception is Outlook on Windows, which renders with Word:
    // Word takes the first name in the stack and, when it is not installed,
    // drops to Times New Roman rather than trying the next one. Outlook readers
    // will see a serif until a developer adds the `mso-font-alt` declaration
    // that tells Word what to use instead. See DEVELOPER_TASKS.md.
    fontFamily: DEFAULT_FONT.stack,
  };
}
